import { useState } from 'react';
import {
  Platform,
  Pressable,
  StyleSheet,
  View,
  useWindowDimensions,
} from 'react-native';
import { observer } from 'mobx-react';
import { router } from 'expo-router';
import { Image } from 'expo-image';
import { LinearGradient } from 'expo-linear-gradient';

import { ThemedText } from '@/components/ThemedText';
import { useStores } from '@/stores/StoreProvider';
import { colors } from '@/constants/colors';
import { workoutTranslations } from '@/constants/workoutTranslations';
import { workoutAssets } from '@/constants/workoutAssets';
import DefaultBackground from '@/components/DefaultBackground';
import TopbarLogo from '@/components/TopbarLogo';

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function getResponsiveMetrics(width: number, height: number) {
  const shortestSide = Math.min(width, height);
  const layoutScale = clamp(shortestSide / 720, 0.5, 1.7);
  const typeScale = clamp(shortestSide / 720, 0.5, 1.65);
  const contentMaxWidth = clamp(width * 0.84, 920, 1560);
  const horizontalPadding = clamp(width * 0.05, 36, 180);
  const typeScaleWidth = clamp(width / 1024, 0.5, 1.65);

  return {
    layoutScale,
    typeScale,
    contentMaxWidth,
    horizontalPadding,
    logoHeight: clamp(48 * layoutScale, 44, 72),
    topBarVerticalPadding: clamp(24 * layoutScale, 20, 36),
    contentBottomPadding: clamp(40 * layoutScale, 32, 72),
    contentGap: clamp(22 * layoutScale, 20, 34),
    buttonGap: clamp(16 * layoutScale, 16, 28),
    buttonVerticalPadding: clamp(18 * layoutScale, 18, 28),
    buttonHorizontalPadding: clamp(28 * layoutScale, 28, 42),
    buttonBorderRadius: clamp(14 * layoutScale, 14, 24),
    buttonTextSize: clamp(22 * typeScale, 24, 34),
    titlePadding: clamp(32 * layoutScale, 14, 42),
    imageTitleSize: clamp(32 * typeScale, 18, 48),
    niceworkSize: clamp(48 * typeScale, 36, 72),
    cardGap: clamp(18 * layoutScale, 16, 30),
    cardRadius: clamp(16 * layoutScale, 16, 28),
    statBoxPadding: clamp(24 * layoutScale, 14, 60),
    statBoxRadius: clamp(12 * layoutScale, 12, 22),
    statBoxGap: clamp(12 * layoutScale, 12, 22),
    statNumberSize: clamp(42 * typeScale, 40, 60),
    statLabelSize: clamp(
      (Platform.OS === 'web' ? 26 : 34) * typeScaleWidth,
      20,
      60,
    ),
    equipmentLabelSize: clamp(24 * typeScale, 14, 60),
    equipmentValueSize: clamp(14 * typeScale, 16, 24),
    equipmentGap: clamp(5 * layoutScale, 5, 10),
    equipmentIconSize: clamp(32 * layoutScale, 28, 52),
    textSize: clamp(16 * typeScale, 18, 24),
  };
}

function getWorkoutTitle(templateType: string | null): string {
  if (!templateType) return 'Workout';
  const translation =
    workoutTranslations[templateType as keyof typeof workoutTranslations] ??
    workoutTranslations['FALLBACK'];
  return translation.title;
}

function getWorkoutAssets(templateType: string | null) {
  if (!templateType) return workoutAssets['DEFAULT'];
  return (
    workoutAssets[templateType as keyof typeof workoutAssets] ??
    workoutAssets['DEFAULT']
  );
}

function CompleteScreen() {
  const { workoutPlayerStore, guidedWorkoutPlayerStore, workoutSessionStore } =
    useStores();
  const { width, height } = useWindowDimensions();
  const metrics = getResponsiveMetrics(width, height);
  const styles = useStyles(width, height);

  // Capture the final stats once on mount so they stay frozen when
  // "Workout Again" calls reset() before the navigation transition finishes —
  // otherwise the observed store nulls out and the stats flash to 0 / 0:00.
  const [stats] = useState(() => {
    const isGuided = workoutSessionStore.mode === 'guided';
    const store = isGuided ? guidedWorkoutPlayerStore : workoutPlayerStore;

    const durationMs =
      store.workoutStartedAt && store.workoutCompletedAt
        ? store.workoutCompletedAt - store.workoutStartedAt
        : 0;
    const minutes = Math.floor(durationMs / 60000);
    const seconds = Math.floor((durationMs % 60000) / 1000);

    return {
      timeDisplay: `${minutes}:${String(seconds).padStart(2, '0')}`,
      count: isGuided
        ? (guidedWorkoutPlayerStore.workout?.exerciseCount ?? 0)
        : (workoutPlayerStore.workout?.totalExercises ?? 0),
      countLabel:
        guidedWorkoutPlayerStore.workout?.exerciseCountLabel ?? 'Exercises',
      title: getWorkoutTitle(workoutSessionStore.templateType),
      imageUri: getWorkoutAssets(workoutSessionStore.templateType).pathInfo,
    };
  });

  const handleWorkoutAgain = () => {
    workoutSessionStore.reset();
    router.dismissTo('/(main)');
  };

  return (
    <View style={styles.container}>
      <DefaultBackground />
      <View style={styles.topBar}>
        <TopbarLogo />
      </View>
      <View style={styles.content}>
        <View style={styles.contentInner}>
          <ThemedText style={styles.niceWork}>Nice Work!</ThemedText>

          <View style={styles.infoCard}>
            <View style={styles.imageContainer}>
              <Image
                style={StyleSheet.absoluteFill}
                contentFit="cover"
                contentPosition="top"
                source={{ uri: stats.imageUri }}
              />
              <LinearGradient
                colors={['transparent', 'rgba(0,0,0,0.65)']}
                style={[StyleSheet.absoluteFill, { top: '40%' }]}
              />
              <View style={styles.imageTitleContainer}>
                <ThemedText style={styles.imageTitle}>{stats.title}</ThemedText>
              </View>
            </View>

            <View style={styles.statsContainer}>
              <View style={styles.statsRow}>
                <View style={styles.statBox}>
                  <ThemedText style={styles.statNumber}>
                    {stats.timeDisplay}
                  </ThemedText>
                  <ThemedText style={styles.statLabel}>Time</ThemedText>
                </View>
                <View style={styles.statBox}>
                  <ThemedText
                    numberOfLines={1}
                    adjustsFontSizeToFit
                    style={styles.statNumber}
                  >
                    {stats.count}
                  </ThemedText>
                  <ThemedText
                    numberOfLines={1}
                    adjustsFontSizeToFit
                    style={styles.statLabel}
                  >
                    {stats.countLabel}
                  </ThemedText>
                </View>
              </View>
              <Pressable
                hasTVPreferredFocus
                style={({ focused }) => [
                  styles.workoutAgainButton,
                  focused && styles.workoutAgainButtonFocused,
                ]}
                onPress={handleWorkoutAgain}
              >
                <ThemedText style={styles.workoutAgainButtonText}>
                  Workout Again
                </ThemedText>
              </Pressable>
            </View>
          </View>
        </View>
      </View>
    </View>
  );
}

export default observer(CompleteScreen);

function useStyles(width: number, height: number) {
  const metrics = getResponsiveMetrics(width, height);

  return StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.brand.primary,
    },
    topBar: {
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      paddingVertical: metrics.topBarVerticalPadding,
      paddingHorizontal: metrics.horizontalPadding,
    },
    content: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      paddingHorizontal: metrics.horizontalPadding,
      paddingBottom: metrics.contentBottomPadding,
    },
    contentInner: {
      width: '100%',
      maxWidth: metrics.contentMaxWidth,
      gap: metrics.contentGap,
    },
    niceWork: {
      color: '#fff',
      fontSize: metrics.niceworkSize,
      fontWeight: '700',
      textAlign: 'center',
      marginBottom: clamp(20 * metrics.layoutScale, 20, 36),
    },
    workoutAgainButton: {
      paddingVertical: metrics.buttonVerticalPadding,
      paddingHorizontal: metrics.buttonHorizontalPadding,
      backgroundColor: '#fff',
      borderRadius: metrics.buttonBorderRadius,
      borderWidth: 2,
      borderColor: '#fff',
      alignItems: 'center',
      shadowColor: '#fff',
      shadowOffset: { width: 0, height: 0 },
      shadowOpacity: 0.4,
      shadowRadius: 8 * metrics.layoutScale,
    },
    workoutAgainButtonFocused: {
      borderColor: colors.brand.primary,
      backgroundColor: 'rgba(255, 255, 255, 0.85)',
    },
    workoutAgainButtonText: {
      color: colors.brand.primary,
      fontSize: metrics.buttonTextSize,
      fontWeight: '700',
    },
    infoCard: {
      flexDirection: 'row',
      width: '100%',
      gap: metrics.cardGap,
    },
    imageContainer: {
      flex: 1.12,
      aspectRatio: 16 / 10,
      position: 'relative',
      borderRadius: metrics.cardRadius,
      justifyContent: 'flex-end',
      overflow: 'hidden',
    },
    imageTitleContainer: {
      padding: metrics.titlePadding,
    },
    imageTitle: {
      color: '#fff',
      fontSize: metrics.imageTitleSize,
      fontWeight: '700',
    },
    statsContainer: {
      flex: 0.88,
      gap: metrics.cardGap,
      justifyContent: 'space-between',
    },
    statsRow: {
      flexDirection: 'row',
      gap: metrics.cardGap,
      flex: 1,
    },
    statBox: {
      flex: 1,
      backgroundColor: 'rgba(255, 255, 255, 0.15)',
      borderRadius: metrics.statBoxRadius,
      justifyContent: 'center',
      alignItems: 'center',
      padding: metrics.statBoxPadding,
      gap: metrics.statBoxGap,
    },
    statNumber: {
      color: '#fff',
      fontSize: metrics.statNumberSize,
      fontWeight: '700',
    },
    statLabel: {
      color: '#fff',
      fontSize: metrics.statLabelSize,
      fontWeight: '600',
    },
  });
}
